import React, { useState, useEffect } from "react";
import { getOrderInformation } from "@actions/sellOrderInfo";
import {
  UPDATE_ORDER_STATUS_LAST_NAME,
  UPDATE_ORDER_STATUS_ID,
  SHOW_ERROR_ORDER_DETAILS
} from "@constants/actionTypes";
import { Helmet } from "react-helmet";
import OrderInformation from "@components/OrderInformation";
import styled from "@emotion/styled";
import loadable from "@loadable/component";
const CardCashSpinner = loadable(() =>
  import(
    /* webpackChunkName: "CardCashSpinner" */ "../components/CardCashSpinner"
  )
);
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { initAppBrowser } from "@actions/initApp";

const ContainerDiv = styled.div`
  min-height: 700px;
`;

const Header = styled.h1`
  font-size: 30px;
  line-height: 1.33;
  text-align: center;
  color: #1f2021;
  padding-top: 56px;
  font-weight: 500;
  @media (max-width: 450px) {
    font-size: 26px;
  }
`;

const BoxContainer = styled.div`
  margin: 0 auto;
  width: 405px;
  height: 466px;
  box-shadow: 0 50px 70px 0 rgba(31, 32, 33, 0.2);
  background-color: #ffffff;
  margin-top: 60px;
  z-index: 1;

  @media (max-width: 700px) {
    width: 390px;
  }
  @media (max-width: 450px) {
    width: 85%;
    height: 429px;
  }
  @media (max-width: 370px) {
    width: 93%;
  }
`;

const InnerBoxContainer = styled.div`
  width: 80%;
  margin: 0 auto;
  text-align: center;
  @media (max-width: 370px) {
    width: 90%;
  }
`;

const Input = styled.input`
  width: 300px;
  height: 60px;
  border-radius: 5px;
  box-shadow: 0 20px 70px 0 rgba(31, 32, 33, 0.12);
  background-color: #ffffff;
  border: 0;
  outline: none;
  color: rgba(31, 32, 33, 0.65);
  font-size: 16px;
  padding-left: 15px;
  margin-bottom: 20px;
  &:focus {
    border: 0;
    outline: none;
  }
  @media (max-width: 450px) {
    width: 100%;
    height: 53px;
  }
`;

const Button = styled.button`
  width: 317px;
  height: 72px;
  border-radius: 36px;
  box-shadow: 0 9px 18px 0 rgba(253, 165, 54, 0.3);
  background-color: #ff9c1b;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 16px;
  font-weight: bold;
  margin-top: 20px;
  &:hover,
  &:disabled,
  &:focus {
    color: #ffffff;
    background-color: #ff9c1b;
    border: 0;
    outline: none;
  }
  @media (max-width: 450px) {
    width: 100%;
    height: 64px;
  }
`;

const MainContainer = styled.div`
  min-height: 800px;
  display: flex;
  align-items: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: right bottom;
  position: relative;
  @media (max-width: 700px) {
    min-height: 850px;
  }
  @media (max-width: 600px) {
    min-height: 750px;
  }
`;

const MainImage = styled.img`
  height: 800px;
  border-radius: 0px 0px 380px 0px;
  overflow: hidden;
  @media (max-width: 700px) {
    height: 850px;
  }
  @media (max-width: 600px) {
    height: 750px;
  }
`;

const ImageDiv = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  border-radius: 0px 0px 380px 0px;
  @media (max-width: 700px) {
    border-radius: 0px 0px 263px 0px;
  }
  @media (max-width: 600px) {
    border-radius: 0px 0px 0px 0px;
  }
`;

const ErrorDiv = styled.div`
  border-radius: 10px;
  background-color: rgba(238, 80, 80, 0.1);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.71;
  color: #ba3f3f;
  height: 61px;
  width: 90%;
  margin: 0 auto;
  @media (max-width: 450px) {
    width: 95%;
    height: 55px;
  }
`;

const InnerErrorDiv = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr;
  align-content: center;
  height: 100%;
  width: 90%;
  margin: 0 auto;
  font-size: 13px;
  text-align: center;
  @media (max-width: 450px) {
    width: 95%;
    grid-template-columns: 15px 1fr;
  }
`;

const IconSpan = styled.span`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorTextSpan = styled.span`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Loader = styled.div`
  background-color: #ffffff;
  position: absolute;
  top: 250px;
  right: 0;
  left: 0;
  border-radius: 30px;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const OrderStatus = () => {
  const [loadedData, setLoadedData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const params = useParams();
  const dispatch = useDispatch();
  const orderStatusId = useSelector(state => state.userActions.orderStatusId);
  const orderStatusLastName = useSelector(
    state => state.userActions.orderStatusLastName
  );
  const showError = useSelector(state => state.orderStatus.showError);

  useEffect(() => {
    dispatch(initAppBrowser(["messages"]));

    window.scrollTo(0, 0);
    const param = params.param;
    if (param) {
      makeApiCall(param);
    } else {
      setIsLoadingData(false);
    }
  }, []);

  const makeApiCall = (firstParam, secondParam) => {
    const response = dispatch(getOrderInformation(firstParam, secondParam));
    response
      .then(res => {
        if (res === "success") {
          setLoadedData(true);
        }
      })
      .catch(err => {
        dispatch({ type: SHOW_ERROR_ORDER_DETAILS, payload: "SHOW_ERROR" });
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoadingData(false);
        }, 1000);
      });
  };

  const checkFields = e => {
    e.preventDefault();

    if (orderStatusId.length && orderStatusLastName.length) {
      makeApiCall(orderStatusId, orderStatusLastName);
    } else {
      dispatch({ type: SHOW_ERROR_ORDER_DETAILS, payload: "SHOW_ERROR" });
    }
  };

  const fadeError = () => {
    window.setTimeout(() => {
      dispatch({ type: SHOW_ERROR_ORDER_DETAILS, payload: "FADE" });
    }, 5000);
  };

  const changeToOtherOrder = () => {
    setLoadedData(false);
  };

  if (showError === "SHOW_ERROR") {
    fadeError();
  }
  return (
    <ContainerDiv>
      {isLoadingData ? (
        <Loader>
          <CardCashSpinner />
        </Loader>
      ) : !loadedData ? (
        <MainContainer>
          <Helmet>
            <title> Order Status | CardCash </title>
          </Helmet>
          <ImageDiv>
            <MainImage
              src="https://cdn.cardcash.com/website/ui/OrderStatus/OrderStatus-Sq.png"
              alt=""
            />
          </ImageDiv>

          <BoxContainer>
            <InnerBoxContainer>
              <Header>Check Order Status</Header>
              <form onSubmit={checkFields}>
                <Input
                  value={orderStatusLastName}
                  placeholder="Last Name"
                  onChange={e =>
                    dispatch({
                      type: UPDATE_ORDER_STATUS_LAST_NAME,
                      payload: e.target.value
                    })
                  }
                  type="text"
                  required
                />
                <Input
                  value={orderStatusId}
                  placeholder="Order Number"
                  onChange={e =>
                    dispatch({
                      type: UPDATE_ORDER_STATUS_ID,
                      payload: e.target.value
                    })
                  }
                  type="text"
                  required
                />
                <Button type="submit">Check Order</Button>
              </form>
            </InnerBoxContainer>
            <ErrorDiv
              className={
                showError === "SHOW_ERROR"
                  ? ""
                  : showError === "FADE"
                  ? "animated fadeOut"
                  : "hide"
              }
            >
              <InnerErrorDiv>
                <IconSpan>
                  <img
                    src="https://cdn.cardcash.com/website/ui/elements/error-icon.svg"
                    alt="error icon"
                  />
                </IconSpan>
                <ErrorTextSpan>
                  The last name and order ID don't match
                </ErrorTextSpan>
              </InnerErrorDiv>
            </ErrorDiv>
          </BoxContainer>
        </MainContainer>
      ) : (
        <OrderInformation changeToOtherOrder={changeToOtherOrder} />
      )}
    </ContainerDiv>
  );
};

export default OrderStatus;
